import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { pickLocalized } from '@/shared/utils/localized';
import type { ApiSupportFaq } from '@/shared/types/api/support';

type Props = {
  faqs: ApiSupportFaq[];
};

export const WidgetsSupportFaqAccordion = ({ faqs }: Props) => {
  const { i18n } = useTranslation('support');
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
      {faqs.map((faq, index) => {
        const question = pickLocalized(faq, 'question', lang);
        const answer = pickLocalized(faq, 'answer', lang);
        const isOpen = openId === faq.id;
        return (
          <div key={faq.id} className={index > 0 ? 'border-t border-slate-100' : undefined}>
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : faq.id)}
              aria-expanded={isOpen}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50"
            >
              <span className="min-w-0 flex-1 text-sm font-medium text-ink">{question}</span>
              <ChevronDownIcon
                className={cn(
                  'h-4 w-4 shrink-0 text-ink-faint transition-transform',
                  isOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
            <div
              className={cn(
                'grid transition-all duration-300 ease-out',
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <div className="whitespace-pre-line px-4 pb-4 text-sm leading-relaxed text-ink-muted">
                  {answer}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
